import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { ASSET_TYPES, uid } from "@/components/wizard/validation";

const emptyAsset = () => ({
  id: uid(),
  type: "Property",
  description: "",
  value: "",
  details: {},
});

// Per-type optional field set. All fields are optional and free-text — they
// flow into the generated will text as a short "Notes" line per asset.
const DETAIL_FIELDS = {
  "Bank Account": [
    ["bank_name", "Bank Name"],
    ["branch", "Branch"],
    ["account_type", "Account Type (Savings / Current / FD)"],
    ["account_last4", "Last 4 digits of account number"],
    ["nominee", "Nominee"],
    ["joint_holder", "Joint holder (if any)"],
  ],
  Property: [
    ["address", "Property address"],
    ["city", "City"],
    ["ownership_type", "Ownership type (Sole / Joint / Co-owned)"],
    ["co_owner", "Co-owner details"],
    ["loan_status", "Loan / Mortgage status"],
  ],
  "Jewellery / Personal Assets": [
    ["locker_number", "Locker number"],
    ["locker_bank", "Bank / Location of locker"],
    ["jewellery_desc", "Description"],
    ["approx_value", "Approximate value (₹)"],
  ],
  Investments: [
    ["broker", "Broker / Platform"],
    ["demat_ref", "Demat reference"],
    ["mf_platform", "Mutual fund platform"],
    ["advisor", "Advisor / CA"],
  ],
  Insurance: [
    ["insurer", "Insurance company"],
    ["policy_number", "Policy number"],
    ["nominee", "Nominee"],
  ],
};

function AssetRow({ a, i, update, remove, disabledRemove }) {
  const [open, setOpen] = useState(false);
  const fields = DETAIL_FIELDS[a.type] || [];
  const filledCount = Object.values(a.details || {}).filter((v) => v && String(v).trim()).length;

  const updateDetail = (key, val) =>
    update(i, "details", { ...(a.details || {}), [key]: val });

  return (
    <Card className="border-[#0A1128]/10" data-testid={`asset-row-${i}`}>
      <CardContent className="grid gap-4 p-5 sm:grid-cols-[200px_1fr_160px_auto]">
        <div>
          <Label>Type *</Label>
          <Select value={a.type} onValueChange={(v) => update(i, "type", v)}>
            <SelectTrigger className="mt-2" data-testid={`asset-type-${i}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSET_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Description *</Label>
          <Input
            className="mt-2"
            value={a.description}
            onChange={(e) => update(i, "description", e.target.value)}
            placeholder="e.g. Flat 4B Sea View, Worli"
            data-testid={`asset-desc-${i}`}
          />
        </div>
        <div>
          <Label>Approx Value</Label>
          <Input
            className="mt-2"
            value={a.value}
            onChange={(e) => update(i, "value", e.target.value)}
            placeholder="Optional (₹)"
            data-testid={`asset-value-${i}`}
          />
        </div>
        <div className="flex items-end">
          <Button
            type="button"
            variant="outline"
            className="mt-2 border-red-200 text-red-600 hover:bg-red-50"
            onClick={() => remove(i)}
            disabled={disabledRemove}
            data-testid={`asset-remove-${i}`}
          >
            <Trash2 size={16} />
          </Button>
        </div>

        {fields.length > 0 && (
          <div className="sm:col-span-4">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-medium text-navy/80 hover:text-navy"
              data-testid={`asset-details-toggle-${i}`}
            >
              {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {open ? "Hide details" : "Add more details"}
              {filledCount > 0 && (
                <span className="ml-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-semibold text-navy">
                  {filledCount} added
                </span>
              )}
              <span className="ml-1 text-[#9CA3AF]">(optional)</span>
            </button>
            {open && (
              <div
                className="mt-3 grid gap-3 rounded-md border border-[#0A1128]/10 bg-[#FBFAF6] p-4 sm:grid-cols-2"
                data-testid={`asset-details-panel-${i}`}
              >
                {fields.map(([key, label]) => (
                  <div key={key}>
                    <Label className="text-xs">{label}</Label>
                    <Input
                      className="mt-1.5"
                      value={(a.details || {})[key] || ""}
                      onChange={(e) => updateDetail(key, e.target.value)}
                      placeholder=""
                      data-testid={`asset-detail-${i}-${key}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AssetsStep({ assets, setAssets }) {
  const add = () => setAssets((a) => [...a, emptyAsset()]);
  const remove = (i) => setAssets((a) => a.filter((_, idx) => idx !== i));
  const update = (i, k, v) =>
    setAssets((a) => a.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  return (
    <div data-testid="step-assets">
      <h2 className="font-serif text-2xl text-navy">Your Assets</h2>
      <p className="mt-2 text-sm text-[#4A5568] sm:text-base">
        List everything you want included in your estate. Every asset must later be assigned
        to at least one beneficiary — the system won't let you skip any. Extra details below
        each asset are optional and help your executor locate things later.
      </p>

      <div className="mt-6 space-y-3">
        {assets.map((a, i) => (
          <AssetRow
            key={a.id}
            a={a}
            i={i}
            update={update}
            remove={remove}
            disabledRemove={assets.length === 1}
          />
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        className="mt-4 border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
        onClick={add}
        data-testid="asset-add-btn"
      >
        <Plus size={16} className="mr-2" /> Add another asset
      </Button>
    </div>
  );
}
